import React, { Fragment, useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { toast } from "react-toastify";

import { load } from "@cashfreepayments/cashfree-js";



import Header from "../components/Header";



import {

  createDonationApi,

  getDonationDetailsByPostApi,

  getPaymentOrderStatusApi,

  getPostByIdApi,

  retryPaymentSessionApi,

} from "../api/DonationActions";



const API_URL = (
  process.env.REACT_APP_API_URL || "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

/* ---------------------------------------------------------------------- */

/* Design tokens — white and blue, anchored on a single deep navy (#0a2354) */

/* with one brighter blue for accents/CTAs so the palette stays disciplined */

/* instead of scattering multiple hues.                                    */

/* ---------------------------------------------------------------------- */

const INK = "#0A2354";

const INK_DARK = "#061733";

const GOLD = "#2F6FE4";

const GOLD_DARK = "#1447B0";

const TEAL = "#1D4ED8";

const CREAM = "#F4F7FC";

const SERIF = "'Fraunces', 'Georgia', serif";

const SANS = "'Inter', system-ui, sans-serif";



const AVATAR_PALETTE = [INK, GOLD, TEAL, "#5B7FBE", "#0F1F3D"];



const getPendingOrderStorageKey = (postId?: string) =>

  `pending_donation_order_id_${postId || "unknown"}`;



const getPendingFormStorageKey = (postId?: string) =>

  `pending_donation_form_${postId || "unknown"}`;



const getMediaUrl = (file?: string | null) => {

  if (!file || !String(file).trim()) return "";



  const value = String(file).trim();



  if (

    value.startsWith("http://") ||

    value.startsWith("https://")

  ) {

    return encodeURI(value);

  }



  if (value.startsWith("/uploads/")) {

    return encodeURI(`${API_URL}${value}`);

  }



  if (value.startsWith("uploads/")) {

    return encodeURI(`${API_URL}/${value}`);

  }



  if (value.startsWith("/")) {

    return encodeURI(`${API_URL}${value}`);

  }



  return encodeURI(`${API_URL}/uploads/${value}`);

};



const getFormattedAmount = (value: number | string) => {

  return `₹${Number(value || 0).toLocaleString("en-IN")}`;

};



const DonatePage: React.FC = () => {

  const { postId } = useParams();

  const navigate = useNavigate();



  const client = useMemo(() => {

    try {

      return JSON.parse(localStorage.getItem("client") || "{}");

    } catch {

      return {};

    }

  }, []);



  const user = useMemo(() => {

    try {

      return JSON.parse(localStorage.getItem("user") || "{}");

    } catch {

      return {};

    }

  }, []);



  const isMemberDonation = Boolean(

    user?.user_id || user?.contact_id

  );



  const [post, setPost] = useState<any>(null);

  const [oldDonations, setOldDonations] = useState<any[]>([]);
  const [donationSummary, setDonationSummary] = useState<{
    total: number | null;
    count: number | null;
  }>({ total: null, count: null });

  const [pageLoading, setPageLoading] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);

  const [pendingOrderId, setPendingOrderId] =

    useState<number | null>(null);



  const [form, setForm] = useState({

    donor_name: "",

    mobile: "",

    email: "",

    address: "",

    amount: "",

    visibility: "anonymous",

    remarks: "",

  });



  const clearPendingDonation = () => {

    setPendingOrderId(null);



    sessionStorage.removeItem(

      getPendingOrderStorageKey(postId)

    );



    sessionStorage.removeItem(

      getPendingFormStorageKey(postId)

    );

  };



  const savePendingDonation = (

    orderId: number,

    currentForm = form

  ) => {

    setPendingOrderId(orderId);



    sessionStorage.setItem(

      getPendingOrderStorageKey(postId),

      String(orderId)

    );



    sessionStorage.setItem(

      getPendingFormStorageKey(postId),

      JSON.stringify({

        donor_name: currentForm.donor_name,

        mobile: currentForm.mobile,

        email: currentForm.email,

        address: currentForm.address,

        amount: currentForm.amount,

        visibility: currentForm.visibility,

        remarks: currentForm.remarks,

      })

    );

  };



  const targetAmount = Number(post?.target_amount || 0);



  // Recent rows can be paginated; never treat their sum as the full campaign total.
  const totalCollected =
    donationSummary.total ?? Number(post?.total_collected_amount || 0);

  const remainingAmount = Math.max(

    targetAmount - totalCollected,

    0

  );



  const progressPercent = targetAmount

    ? Math.min(

        100,

        Math.round(

          (totalCollected / targetAmount) * 100

        )

      )

    : 0;



  const fetchDetails = async () => {

    if (!postId) return;



    try {

      setPageLoading(true);



      const postRes = await getPostByIdApi(postId);

      const postData = postRes?.data || null;



      setPost(postData);



      if (postData?.client_id) {

        const donationRes =

          await getDonationDetailsByPostApi(

            postId,

            postData.client_id

          );



        // The legacy endpoint response schema has not been supplied.
        // Accept common row envelopes without inventing aggregate totals.
        const body = donationRes?.data;
        const payload = body?.data && !Array.isArray(body.data) ? body.data : body;
        const rows = Array.isArray(body) ? body
          : Array.isArray(payload?.transactions) ? payload.transactions
          : Array.isArray(payload?.rows) ? payload.rows
          : Array.isArray(payload?.donations) ? payload.donations
          : Array.isArray(payload?.data) ? payload.data
          : [];

        const donationRows = rows.map((item: any) => ({
          ...item,
          // Never reveal anonymous donors through the public interface.
          donor_name: String(item.visibility || "").toLowerCase() === "anonymous"
            ? "Anonymous Donor"
            : item.donor_name || item.payer_name || item.member_name || item.full_name,
        }));

        const numericSummary = (value: any) =>
          value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value))
            ? Number(value)
            : null;

        setDonationSummary({
          total: numericSummary(payload?.total_collected ?? payload?.total_collected_amount ?? payload?.summary?.total_collected),
          count: numericSummary(payload?.donor_count ?? payload?.total_donors ?? payload?.summary?.donor_count),
        });
        setOldDonations(donationRows);

      } else {

        setOldDonations([]);

      }

    } catch (error) {

      console.error(error);

      toast.error("Failed to load donation details");

    } finally {

      setPageLoading(false);

    }

  };



  useEffect(() => {

    const savedOrderId = sessionStorage.getItem(

      getPendingOrderStorageKey(postId)

    );



    const savedForm = sessionStorage.getItem(

      getPendingFormStorageKey(postId)

    );



    if (savedOrderId) {

      const parsedOrderId = Number(savedOrderId);



      if (

        Number.isFinite(parsedOrderId) &&

        parsedOrderId > 0

      ) {

        setPendingOrderId(parsedOrderId);

      } else {

        sessionStorage.removeItem(

          getPendingOrderStorageKey(postId)

        );

      }

    }



    if (savedForm) {

      try {

        const parsed = JSON.parse(savedForm);



        setForm((previous) => ({

          ...previous,

          donor_name: parsed?.donor_name || "",

          mobile: parsed?.mobile || "",

          email: parsed?.email || "",

          address: parsed?.address || "",

          amount: parsed?.amount || "",

          visibility:

            parsed?.visibility === "public"

              ? "public"

              : "anonymous",

          remarks: parsed?.remarks || "",

        }));

      } catch {

        sessionStorage.removeItem(

          getPendingFormStorageKey(postId)

        );

      }

    }

  }, [postId]);



  useEffect(() => {

    fetchDetails();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [postId]);



  const handleChange = (

    e: React.ChangeEvent<

      HTMLInputElement |

      HTMLSelectElement |

      HTMLTextAreaElement

    >

  ) => {

    setForm((prev) => ({

      ...prev,

      [e.target.name]: e.target.value,

    }));

  };



  const handleVisibilityChange = (

    e: React.ChangeEvent<HTMLInputElement>

  ) => {

    setForm((prev) => ({

      ...prev,

      visibility: e.target.checked

        ? "anonymous"

        : "public",

    }));

  };



  const getMemberName = () => {

    return (

      client?.full_name ||

      client?.client_name ||

      user?.user_fullname ||

      user?.full_name ||

      user?.name ||

      user?.contact_name ||

      "Member"

    );

  };



  const getMemberContact = () => {

    return (

      client?.email ||

      user?.user_email ||

      user?.email ||

      user?.phone_number ||

      client?.client_mobile ||

      client?.mobile ||

      user?.user_mobile ||

      user?.mobile ||

      localStorage.getItem("public_mobile") ||

      "Logged-in donor"

    );

  };



  const handleSubmit = async (

    e: React.FormEvent

  ) => {

    e.preventDefault();



    if (!post) {

      toast.error("Post not found");

      return;

    }



    if (

      !form.amount ||

      Number(form.amount) <= 0

    ) {

      toast.error("Please enter valid amount");

      return;

    }



    if (

      !isMemberDonation &&

      !form.donor_name.trim()

    ) {

      toast.error("Please enter donor name");

      return;

    }



    if (

      !isMemberDonation &&

      !form.mobile.trim()

    ) {

      toast.error("Please enter mobile number");

      return;

    }



    const donorName = isMemberDonation

      ? getMemberName()

      : form.donor_name.trim();



    const donorMobile = isMemberDonation

      ? user?.phone_number ||

        client?.client_mobile ||

        client?.mobile ||

        user?.user_mobile ||

        user?.mobile ||

        localStorage.getItem("public_mobile") ||

        null

      : form.mobile.trim();



    if (!donorMobile) {

      toast.error(

        "Mobile number is required to continue payment"

      );

      return;

    }



    const donorEmail = isMemberDonation

      ? client?.email ||

        user?.user_email ||

        user?.email ||

        null

      : form.email.trim() || null;



    const amount = Number(form.amount);



    const payload = {

      transaction_type: "donation",



      client_id: post.client_id,

      post_id: post.post_id,



      user_id:

        Number(user?.user_id) || null,

      contact_id:

        Number(user?.contact_id) || null,



      payer_type: user?.user_id

        ? "user"

        : user?.contact_id

        ? "contact"

        : "guest",



      payer_name: donorName,

      payer_email: donorEmail,

      payer_mobile: donorMobile,



      donor_name: donorName,

      mobile: donorMobile,

      email: donorEmail,



      address: isMemberDonation

        ? null

        : form.address.trim() || null,



      reference_type: "post",

      reference_id: post.post_id,



      amount,

      payable_amount: amount,

      tax_amount: 0,

      discount_amount: 0,

      currency: "INR",



      donation_source: isMemberDonation

        ? "member"

        : "guest",



      visibility: form.visibility,



      source_app: "public_web",

      source_platform: "web",



      remarks:

        form.remarks ||

        `Donation for ${post.title}`,



      return_url:

        `${window.location.origin}/payment/return`,



      notify_url:

        `${API_URL}/billing-finance/webhooks/cashfree/payment`,

    };



    try {

      setSubmitLoading(true);



      const existingOrderId =

        pendingOrderId;



      let res: any;



      if (existingOrderId) {

        const statusRes =

          await getPaymentOrderStatusApi(

            existingOrderId

          );



        const currentStatus = String(

          statusRes?.data?.order_status ||

          statusRes?.data?.status ||

          ""

        ).toLowerCase();



        if (currentStatus === "paid") {

          clearPendingDonation();



          navigate(

            `/payment/return?order_id=${existingOrderId}`

          );



          return;

        }



        res = await retryPaymentSessionApi(

          existingOrderId,

          {

            return_url:

              payload.return_url,

            notify_url:

              payload.notify_url,

          }

        );

      } else {

        res =

          await createDonationApi(

            payload

          );

      }



      const responseOrderId = Number(

        res?.data?.order_id ||

        res?.data?.order?.order_id ||

        existingOrderId ||

        0

      );



      if (responseOrderId > 0) {

        savePendingDonation(

          responseOrderId,

          form

        );

      }



      if (

        res?.status === true &&

        res?.data?.payment_session_id

      ) {

        const gatewayEnvironment =

          res?.data?.gateway?.environment ||

          res?.data?.session?.gateway

            ?.environment ||

          res?.data?.order?.gateway

            ?.environment ||

          "test";



        const cashfree = await load({

          mode:

            gatewayEnvironment ===

            "production"

              ? "production"

              : "sandbox",

        });



        await cashfree.checkout({

          paymentSessionId:

            res.data.payment_session_id,

          redirectTarget: "_self",

        });



        return;

      }



      toast.error(

        res?.message ||

        "Unable to start payment. Please try again."

      );

    } catch (error: any) {

      console.error(

        "Donation payment error:",

        error

      );



      toast.error(

        error?.response?.data?.message ||

        error?.message ||

        "Donation failed"

      );

    } finally {

      setSubmitLoading(false);

    }

  };



  if (pageLoading) {

    return (

      <PageShell>

        <div className="flex min-h-[240px] flex-col items-center justify-center rounded-[20px] border border-[#DCE4F5] bg-white p-6 text-center shadow-[0_8px_30px_rgba(22,50,79,0.06)]">

          <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-[#DCE4F5] border-t-[#2F6FE4]" />



          <p

            className="mt-4 text-sm font-medium text-[#6B7280]"

            style={{ fontFamily: SANS }}

          >

            Loading donation details…

          </p>

        </div>

      </PageShell>

    );

  }



  if (!post) {

    return (

      <PageShell>

        <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[20px] border border-[#DCE4F5] bg-white p-6 text-center shadow-[0_8px_30px_rgba(22,50,79,0.06)]">

          <div

            className="mb-4 flex h-14 w-14 items-center justify-center rounded-full text-2xl"

            style={{

              backgroundColor:

                "rgba(22,50,79,0.06)",

              color: INK,

            }}

          >

            <i className="feather-alert-circle" />

          </div>



          <h4

            className="text-lg font-semibold text-[#0A2354]"

            style={{ fontFamily: SERIF }}

          >

            This donation post couldn't be found

          </h4>



          <p

            className="mt-2 text-sm text-[#6B7280]"

            style={{ fontFamily: SANS }}

          >

            It may have been removed or the link is incorrect.

          </p>



          <button

            type="button"

            onClick={() => navigate(-1)}

            className="mt-5 rounded-full px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"

            style={{

              backgroundColor: INK,

              fontFamily: SANS,

            }}

          >

            Go back

          </button>

        </div>

      </PageShell>

    );

  }



  const donorCount =

    oldDonations.length;



  return (

    <PageShell>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_400px]">

        <main className="space-y-5">

          <section className="overflow-hidden rounded-[22px] border border-[#DCE4F5] bg-white shadow-[0_10px_34px_rgba(22,50,79,0.07)]">

            <div className="relative overflow-hidden bg-[#EEF3FB]">

              {post.post_image ? (

                <img

                  src={getMediaUrl(

                    post.post_image

                  )}

                  alt={

                    post.title ||

                    "Donation post"

                  }

                  className="block h-auto max-h-[520px] w-full object-contain"

                />

              ) : (

                <div

                  className="flex h-[300px] w-full items-center justify-center text-5xl"

                  style={{

                    backgroundColor:

                      "rgba(22,50,79,0.06)",

                    color: INK,

                  }}

                >

                  <i className="feather-heart" />

                </div>

              )}



              <div className="absolute left-4 top-4 sm:left-5 sm:top-5">

                <span

                  className="inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/95 px-3.5 py-1.5 text-xs font-bold tracking-wide shadow-sm backdrop-blur"

                  style={{

                    color: GOLD_DARK,

                    fontFamily: SANS,

                  }}

                >

                  <i className="feather-award text-sm" />

                  {post.category_name ||

                    "Donation"}

                </span>

              </div>

            </div>



            <div className="p-5 sm:p-7">

              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#6B7280]">

                <span

                  className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF4FF] px-3 py-1.5"

                  style={{

                    color: GOLD_DARK,

                    fontFamily: SANS,

                  }}

                >

                  <i className="feather-heart" />

                  Donation campaign

                </span>



                {post.unique_slug && (

                  <span

                    className="rounded-full bg-[#F4F7FC] px-3 py-1.5"

                    style={{

                      fontFamily: SANS,

                    }}

                  >

                    {post.unique_slug}

                  </span>

                )}

              </div>



              <h1

                className="mt-4 text-[26px] font-semibold leading-[1.2] text-[#0A2354] sm:text-[34px]"

                style={{

                  fontFamily: SERIF,

                }}

              >

                {post.title ||

                  "Donation"}

              </h1>



              {post.short_description && (

                <p

                  className="mt-3 max-w-3xl text-[15px] leading-7 text-[#5B6B8C]"

                  style={{

                    fontFamily: SANS,

                  }}

                >

                  {

                    post.short_description

                  }

                </p>

              )}



              <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-[#E3EAF8] pt-4">

                <div className="flex min-w-0 items-center gap-3">

                  {post.logo_image ? (

                    <img

                      src={getMediaUrl(

                        post.logo_image

                      )}

                      alt={

                        post.client_name ||

                        "Club"

                      }

                      className="h-11 w-11 shrink-0 rounded-full border border-[#DCE4F5] bg-white object-cover"

                    />

                  ) : (

                    <div

                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"

                      style={{

                        backgroundColor:

                          INK,

                        fontFamily: SANS,

                      }}

                    >

                      {String(

                        post.client_name ||

                          "C"

                      )

                        .charAt(0)

                        .toUpperCase()}

                    </div>

                  )}



                  <div className="min-w-0">

                    <span

                      className="block text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]"

                      style={{

                        fontFamily: SANS,

                      }}

                    >

                      Organised by

                    </span>



                    <strong

                      className="block truncate text-sm font-semibold text-[#0A2354]"

                      style={{

                        fontFamily: SANS,

                      }}

                    >

                      {post.client_name ||

                        "Club"}

                    </strong>

                  </div>

                </div>



                <div

                  className="inline-flex items-center gap-2 rounded-full bg-[#F4F7FC] px-3.5 py-2 text-xs font-semibold text-[#5B6B8C]"

                  style={{

                    fontFamily: SANS,

                  }}

                >

                  <i className="feather-calendar" />



                  {post.created_at

                    ? new Date(

                        post.created_at

                      ).toLocaleDateString(

                        "en-IN",

                        {

                          day: "2-digit",

                          month: "short",

                          year: "numeric",

                        }

                      )

                    : "Date unavailable"}

                </div>

              </div>

            </div>

          </section>



          <section className="rounded-[22px] border border-[#DCE4F5] bg-white p-5 shadow-[0_6px_24px_rgba(22,50,79,0.05)] sm:p-6">

            <div className="mb-5 flex flex-wrap items-center justify-between gap-5">

              <div className="flex items-center gap-4">

                <ProgressRing

                  percent={

                    progressPercent

                  }

                />



                <div>

                  <span

                    className="text-xs font-bold uppercase tracking-[0.08em]"

                    style={{

                      color: GOLD_DARK,

                      fontFamily: SANS,

                    }}

                  >

                    Campaign progress

                  </span>



                  <h3

                    className="mt-1 text-xl font-semibold text-[#0A2354]"

                    style={{

                      fontFamily: SERIF,

                    }}

                  >

                    {getFormattedAmount(

                      totalCollected

                    )}{" "}

                    raised

                  </h3>



                  <p

                    className="mt-0.5 text-sm text-[#6B7280]"

                    style={{

                      fontFamily: SANS,

                    }}

                  >

                    of{" "}

                    {getFormattedAmount(

                      targetAmount

                    )}{" "}

                    goal

                  </p>

                </div>

              </div>



              {donorCount > 0 && (

                <div className="flex items-center gap-3">

                  <AvatarStack

                    donations={

                      oldDonations

                    }

                  />



                  <p

                    className="text-sm text-[#6B7280]"

                    style={{

                      fontFamily: SANS,

                    }}

                  >

                    <strong className="text-[#0A2354]">

                      {donorCount}

                    </strong>{" "}

                    {donorCount === 1

                      ? "person has"

                      : "people have"}{" "}

                    donated

                  </p>

                </div>

              )}

            </div>



            <div className="grid gap-3 sm:grid-cols-3">

              <StatBox

                label="Target"

                value={getFormattedAmount(

                  targetAmount

                )}

                icon="feather-target"

              />



              <StatBox

                label="Collected"

                value={getFormattedAmount(

                  totalCollected

                )}

                icon="feather-check-circle"

                accent={TEAL}

              />



              <StatBox

                label="Remaining"

                value={getFormattedAmount(

                  remainingAmount

                )}

                icon="feather-clock"

              />

            </div>

          </section>



          <section className="rounded-[22px] border border-[#DCE4F5] bg-white p-5 shadow-[0_6px_24px_rgba(22,50,79,0.05)] sm:p-6">

            <div className="mb-5">

              <span

                className="text-xs font-bold uppercase tracking-[0.08em]"

                style={{

                  color: GOLD_DARK,

                  fontFamily: SANS,

                }}

              >

                Supporters

              </span>



              <h3

                className="mt-1 text-xl font-semibold text-[#0A2354]"

                style={{

                  fontFamily: SERIF,

                }}

              >

                Recent donations

              </h3>

            </div>



            {oldDonations.length === 0 ? (

              <div className="flex min-h-[180px] flex-col items-center justify-center rounded-[18px] bg-[#F4F7FC] text-center">

                <div

                  className="mb-3 flex h-14 w-14 items-center justify-center rounded-full text-2xl"

                  style={{

                    backgroundColor:

                      "rgba(22,50,79,0.06)",

                    color: INK,

                  }}

                >

                  <i className="feather-gift" />

                </div>



                <h4

                  className="text-base font-semibold text-[#0A2354]"

                  style={{

                    fontFamily: SERIF,

                  }}

                >

                  No recent donations

                </h4>



                <p

                  className="mt-1 text-sm text-[#6B7280]"

                  style={{

                    fontFamily: SANS,

                  }}

                >

                  Be the first person to support this cause.

                </p>

              </div>

            ) : (

              <div className="recent-donations-scroll max-h-[430px] divide-y divide-[#E3EAF8] overflow-y-auto pr-2">

                {oldDonations.map(

                  (item, idx) => {

                    const donorName =

                      item.visibility ===

                      "anonymous"

                        ? "Anonymous Donor"

                        : item.donor_name ||

                          item.payer_name ||

                          item.member_name ||

                          item.full_name ||

                          (item.donation_source ===

                            "guest" ||

                          item.payer_type ===

                            "guest"

                            ? "Guest Donor"

                            : "Member");



                    const avatarColor =

                      AVATAR_PALETTE[

                        idx %

                          AVATAR_PALETTE.length

                      ];



                    return (

                      <div

                        key={

                          item.transaction_id

                        }

                        className="flex items-center justify-between gap-3 py-3.5 first:pt-0 last:pb-0"

                      >

                        <div className="flex min-w-0 items-center gap-3.5">

                          <div

                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"

                            style={{

                              backgroundColor:

                                item.visibility ===

                                "anonymous"

                                  ? "#9CA3AF"

                                  : avatarColor,

                              fontFamily:

                                SANS,

                            }}

                          >

                            {item.visibility ===

                            "anonymous"

                              ? "A"

                              : String(

                                  donorName

                                )

                                  .charAt(

                                    0

                                  )

                                  .toUpperCase()}

                          </div>



                          <div className="min-w-0">

                            <strong

                              className="block truncate text-sm font-semibold text-[#0A2354]"

                              style={{

                                fontFamily:

                                  SANS,

                              }}

                            >

                              {

                                donorName

                              }

                            </strong>



                            <p

                              className="mt-0.5 truncate text-xs text-[#8A93A3]"

                              style={{

                                fontFamily:

                                  SANS,

                              }}

                            >

                              {item.payment_method_name ||

                                item.method_name ||

                                item.payment_method_code ||

                                "Payment"}{" "}

                              ·{" "}

                              {item.transaction_date

                                ? new Date(

                                    item.transaction_date

                                  ).toLocaleString(

                                    "en-IN"

                                  )

                                : "Date not available"}

                            </p>

                          </div>

                        </div>



                        <span

                          className="shrink-0 rounded-full px-3 py-1 text-xs font-bold"

                          style={{

                            backgroundColor:

                              "rgba(42,140,130,0.10)",

                            color: TEAL,

                            fontFamily:

                              SANS,

                          }}

                        >

                          {getFormattedAmount(

                            item.amount ||

                              0

                          )}

                        </span>

                      </div>

                    );

                  }

                )}

              </div>

            )}

          </section>

        </main>



        <aside className="xl:sticky xl:top-24 xl:self-start">

          <section className="overflow-hidden rounded-[22px] border border-[#DCE4F5] bg-white shadow-[0_14px_40px_rgba(22,50,79,0.10)]">

            <div

              className="relative overflow-hidden px-6 py-6"

              style={{

                background:

                  "linear-gradient(135deg, #EAF1FF, #F7FAFF)",

                borderBottom:

                  "1px solid #DCE4F5",

              }}

            >

              <div

                className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full"

                style={{

                  backgroundColor:

                    "rgba(47,111,228,0.08)",

                }}

              />



              <span

                className="relative inline-flex rounded-full px-3.5 py-1.5 text-xs font-bold tracking-wide"

                style={{

                  backgroundColor:

                    "rgba(47,111,228,0.12)",

                  color: GOLD_DARK,

                  fontFamily: SANS,

                }}

              >

                Give today

              </span>



              <h3

                className="relative mt-3 text-2xl font-semibold"

                style={{

                  color: INK,

                  fontFamily: SERIF,

                }}

              >

                Make a donation

              </h3>



              <p

                className="relative mt-1.5 text-sm"

                style={{

                  color: "#5B6B8C",

                  fontFamily: SANS,

                }}

              >

                {isMemberDonation

                  ? "You're donating as a logged-in member."

                  : "Enter your details to complete your donation."}

              </p>

            </div>



            <form

              onSubmit={handleSubmit}

              className="space-y-4 p-6"

            >

              {isMemberDonation ? (

                <div className="rounded-[16px] border border-[#DCE4F5] bg-[#F4F7FC] p-3.5">

                  <div className="mb-3 flex min-w-0 items-center gap-3">

                    <div

                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"

                      style={{

                        backgroundColor:

                          INK,

                        fontFamily: SANS,

                      }}

                    >

                      {String(

                        getMemberName()

                      )

                        .charAt(0)

                        .toUpperCase()}

                    </div>



                    <div className="min-w-0">

                      <span

                        className="block text-[10px] font-bold uppercase tracking-wide text-[#9CA3AF]"

                        style={{

                          fontFamily:

                            SANS,

                        }}

                      >

                        Donating as

                      </span>



                      <strong

                        className="block truncate text-sm font-semibold text-[#0A2354]"

                        style={{

                          fontFamily:

                            SANS,

                        }}

                      >

                        {getMemberName()}

                      </strong>



                      <p

                        className="truncate text-xs text-[#8A93A3]"

                        style={{

                          fontFamily:

                            SANS,

                        }}

                      >

                        {getMemberContact()}

                      </p>

                    </div>

                  </div>



                  <AnonymousToggle

                    checked={

                      form.visibility ===

                      "anonymous"

                    }

                    onChange={

                      handleVisibilityChange

                    }

                    visibility={

                      form.visibility

                    }

                  />

                </div>

              ) : (

                <div className="space-y-3.5">

                  <InputField

                    label="Donor name *"

                    name="donor_name"

                    value={

                      form.donor_name

                    }

                    onChange={

                      handleChange

                    }

                    placeholder="Enter your name"

                  />



                  <InputField

                    label="Mobile number *"

                    name="mobile"

                    value={

                      form.mobile

                    }

                    onChange={

                      handleChange

                    }

                    placeholder="Enter mobile number"

                  />



                  <InputField

                    label="Email"

                    name="email"

                    value={

                      form.email

                    }

                    onChange={

                      handleChange

                    }

                    placeholder="Enter email"

                  />



                  <InputField

                    label="Address"

                    name="address"

                    value={

                      form.address

                    }

                    onChange={

                      handleChange

                    }

                    placeholder="Enter address"

                  />



                  <div className="rounded-[16px] border border-[#DCE4F5] bg-[#F4F7FC] p-3.5">

                    <AnonymousToggle

                      checked={

                        form.visibility ===

                        "anonymous"

                      }

                      onChange={

                        handleVisibilityChange

                      }

                      visibility={

                        form.visibility

                      }

                    />

                  </div>

                </div>

              )}



              <InputField

                label="Amount *"

                type="number"

                name="amount"

                value={form.amount}

                onChange={

                  handleChange

                }

                placeholder="Enter amount"

                prefix="₹"

              />



              <div>

                <label

                  className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6B7280]"

                  style={{

                    fontFamily: SANS,

                  }}

                >

                  Remarks

                </label>



                <textarea

                  name="remarks"

                  value={

                    form.remarks

                  }

                  onChange={

                    handleChange

                  }

                  placeholder="Remarks (optional)"

                  rows={3}

                  maxLength={200}

                  className="min-h-[82px] w-full resize-y rounded-xl border border-[#D7E1F5] bg-white px-3 py-2.5 text-sm font-medium text-[#0A2354] outline-none transition placeholder:text-[#B5AD9C] focus:border-[#2F6FE4] focus:ring-4 focus:ring-[#2F6FE4]/15"

                  style={{

                    fontFamily: SANS,

                  }}

                />



                <small

                  className="mt-1 block text-right text-xs text-[#B5AD9C]"

                  style={{

                    fontFamily: SANS,

                  }}

                >

                  {form.remarks.length}/200

                </small>

              </div>



              {pendingOrderId && (

                <div className="rounded-xl border border-[#F3D58B] bg-[#FFF9E8] px-3.5 py-3">

                  <p

                    className="text-xs font-semibold text-[#8A6414]"

                    style={{

                      fontFamily: SANS,

                    }}

                  >

                    Payment was not completed. Retry using the same order.

                  </p>

                </div>

              )}



              <button

                type="submit"

                disabled={

                  submitLoading

                }

                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white shadow-[0_6px_18px_rgba(201,161,90,0.35)] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"

                style={{

                  background: `linear-gradient(135deg, ${GOLD_DARK}, ${GOLD})`,

                  fontFamily: SANS,

                }}

              >

                {submitLoading

                  ? "Starting payment…"

                  : pendingOrderId

                  ? "Retry payment"

                  : "Proceed to payment"}

              </button>



              <p

                className="flex items-center justify-center gap-1.5 text-center text-xs text-[#9CA3AF]"

                style={{

                  fontFamily: SANS,

                }}

              >

                <i className="feather-lock text-[13px]" />

                Secure donation · Receipt sent to your email

              </p>

            </form>

          </section>

        </aside>

      </div>

    </PageShell>

  );

};



const PageShell = ({

  children,

}: {

  children: React.ReactNode;

}) => {

  return (

    <Fragment>

      <Header />



      <div

        className="donate-page-wrap min-h-screen"

        style={{

          marginLeft: "300px",

          width:

            "calc(100% - 300px)",

          paddingTop: "90px",

          paddingLeft: "18px",

          paddingRight: "18px",

          paddingBottom: "24px",

          backgroundColor: CREAM,

        }}

      >

        <div className="mx-auto w-full max-w-[1180px]">

          {children}

        </div>

      </div>



      <style>{`

        .donate-page-wrap {

          position: relative;

          z-index: 1;

        }



        .recent-donations-scroll {

          scrollbar-width: thin;

          scrollbar-color: #BFDBFE #F4F7FC;

        }



        .recent-donations-scroll::-webkit-scrollbar {

          width: 7px;

        }



        .recent-donations-scroll::-webkit-scrollbar-track {

          background: #F4F7FC;

          border-radius: 999px;

        }



        .recent-donations-scroll::-webkit-scrollbar-thumb {

          background: #BFDBFE;

          border-radius: 999px;

          border: 1px solid #EFF6FF;

        }



        .recent-donations-scroll::-webkit-scrollbar-thumb:hover {

          background: #93C5FD;

        }



        @media (max-width: 1200px) {

          .donate-page-wrap {

            margin-left: 0 !important;

            width: 100% !important;

            padding-left: 16px !important;

            padding-right: 16px !important;

            padding-top: 84px !important;

          }

        }



        @media (max-width: 575px) {

          .donate-page-wrap {

            margin-left: 0 !important;

            width: 100% !important;

            padding-left: 12px !important;

            padding-right: 12px !important;

            padding-top: 18px !important;

            padding-bottom: 20px !important;

          }

        }

      `}</style>

    </Fragment>

  );

};



const ProgressRing = ({

  percent,

}: {

  percent: number;

}) => {

  const radius = 30;

  const circumference =

    2 * Math.PI * radius;



  const offset =

    circumference -

    (percent / 100) *

      circumference;



  return (

    <div className="relative flex h-[72px] w-[72px] shrink-0 items-center justify-center">

      <svg

        width="72"

        height="72"

        viewBox="0 0 72 72"

        className="-rotate-90"

      >

        <circle

          cx="36"

          cy="36"

          r={radius}

          fill="none"

          stroke="#E3EAF8"

          strokeWidth="7"

        />



        <circle

          cx="36"

          cy="36"

          r={radius}

          fill="none"

          stroke={GOLD}

          strokeWidth="7"

          strokeLinecap="round"

          strokeDasharray={

            circumference

          }

          strokeDashoffset={

            offset

          }

        />

      </svg>



      <span

        className="absolute text-sm font-bold text-[#0A2354]"

        style={{

          fontFamily: SANS,

        }}

      >

        {percent}%

      </span>

    </div>

  );

};



const AvatarStack = ({

  donations,

}: {

  donations: any[];

}) => {

  const visible =

    donations.slice(0, 4);



  return (

    <div className="flex -space-x-2.5">

      {visible.map(

        (item, idx) => {

          const name =

            item.visibility ===

            "anonymous"

              ? "A"

              : String(

                  item.donor_name ||

                    item.payer_name ||

                    item.member_name ||

                    item.full_name ||

                    (item.donation_source ===

                      "guest" ||

                    item.payer_type ===

                      "guest"

                      ? "G"

                      : "M")

                )

                  .charAt(0)

                  .toUpperCase();



          return (

            <div

              key={

                item.transaction_id ||

                idx

              }

              className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-[11px] font-bold text-white"

              style={{

                backgroundColor:

                  item.visibility ===

                  "anonymous"

                    ? "#9CA3AF"

                    : AVATAR_PALETTE[

                        idx %

                          AVATAR_PALETTE.length

                      ],

                fontFamily:

                  SANS,

              }}

            >

              {name}

            </div>

          );

        }

      )}

    </div>

  );

};



const StatBox = ({

  icon,

  label,

  value,

  accent = INK,

}: {

  icon: string;

  label: string;

  value: string;

  accent?: string;

}) => {

  return (

    <div className="rounded-[16px] border border-[#DCE4F5] bg-[#F4F7FC] p-4">

      <div

        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full text-lg"

        style={{

          backgroundColor: `${accent}14`,

          color: accent,

        }}

      >

        <i className={icon} />

      </div>



      <span

        className="block text-xs font-bold uppercase tracking-wide text-[#9CA3AF]"

        style={{

          fontFamily: SANS,

        }}

      >

        {label}

      </span>



      <strong

        className="mt-1 block truncate text-base font-semibold text-[#0A2354]"

        style={{

          fontFamily: SERIF,

        }}

      >

        {value}

      </strong>

    </div>

  );

};



const InputField = ({

  label,

  name,

  value,

  onChange,

  placeholder,

  type = "text",

  prefix,

}: {

  label: string;

  name: string;

  value: string;

  onChange:

    React.ChangeEventHandler<HTMLInputElement>;

  placeholder: string;

  type?: string;

  prefix?: string;

}) => {

  return (

    <div>

      <label

        className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-[#6B7280]"

        style={{

          fontFamily: SANS,

        }}

      >

        {label}

      </label>



      <div className="relative">

        {prefix && (

          <span

            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#9CA3AF]"

            style={{

              fontFamily: SANS,

            }}

          >

            {prefix}

          </span>

        )}



        <input

          type={type}

          name={name}

          value={value}

          onChange={onChange}

          placeholder={

            placeholder

          }

          min={

            type === "number"

              ? "1"

              : undefined

          }

          inputMode={

            type === "number"

              ? "decimal"

              : undefined

          }

          className={`h-11 w-full rounded-xl border border-[#D7E1F5] bg-white ${

            prefix

              ? "pl-8 pr-3"

              : "px-3"

          } text-sm font-medium text-[#0A2354] outline-none transition placeholder:text-[#B5AD9C] focus:border-[#2F6FE4] focus:ring-4 focus:ring-[#2F6FE4]/15`}

          style={{

            fontFamily: SANS,

          }}

        />

      </div>

    </div>

  );

};



const AnonymousToggle = ({

  checked,

  onChange,

  visibility,

}: {

  checked: boolean;

  onChange:

    React.ChangeEventHandler<HTMLInputElement>;

  visibility: string;

}) => {

  return (

    <label className="flex cursor-pointer select-none items-center gap-3">

      <input

        type="checkbox"

        checked={checked}

        onChange={onChange}

        className="peer sr-only"

      />



      <span

        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 text-transparent transition peer-checked:text-white"

        style={{

          borderColor: checked

            ? GOLD

            : "#D7E1F5",

          backgroundColor:

            checked

              ? GOLD

              : "white",

        }}

      >

        <i className="feather-check text-xs" />

      </span>



      <span className="min-w-0">

        <strong

          className="block text-sm font-semibold leading-tight text-[#0A2354]"

          style={{

            fontFamily: SANS,

          }}

        >

          Anonymous donation

        </strong>



        <small

          className="mt-0.5 block text-xs leading-tight text-[#8A93A3]"

          style={{

            fontFamily: SANS,

          }}

        >

          {visibility ===

          "anonymous"

            ? "Your name will be hidden"

            : "Your name will be shown"}

        </small>

      </span>

    </label>

  );

};



export default DonatePage;
